// usePageAgent — the page side of a page helper chat (Douglas plan T7).
//
// A page helper is an ordinary agent chat created with the page's profile
// (`page-<key>`), plus the page's tools on every message. The agent's calls of
// those tools arrive as page_tool_call events; this hook runs each one here,
// on the page, and posts the result back so the agent reads it as the tool's
// result (plan D5). The rules:
//
// - Every call runs at most once. A call already in the session when this hook
//   first sees it (a restore, a reload) is never run; a call that a reconnect
//   drops and replays (rewindToTurnBase, then /reconnect) is the same
//   toolCallId and is not run again.
// - An ask, click or costly tool is held for the person's Confirm or Decline
//   (D11): nothing the model sends confirms it.
// - A card goes when its call is over without it: the call ended (timed out,
//   the turn was stopped) or the turn ended. Its Confirm then does nothing,
//   since the agent has already been told the call failed.
// - While a call is held, the stuck banner stays off (setPageCallWaiting).
// - An ask tool's card says what the call means in plain words: the tool is
//   run once unconfirmed (which only describes, never acts) and its `meaning`
//   goes on the card.
// - Closing the helper answers every call still held with "The helper was
//   closed." (the turn carries on; its result is in History). Stop answers
//   them with "The turn was stopped." and cancels the turn.
// - The chat is made when the person first sends, not when the helper opens:
//   opening and reading the page's own rows (a table's summary) makes no chat,
//   so nothing reaches History or the bell until they say something. The
//   first message waits for the new chat and goes as soon as it is there.
//   New chat makes one at once.
// - Closing keeps the chat. Reopening on the same spot (the host's resumeKey,
//   e.g. the open table) carries on with it; reopened anywhere else, or with
//   no resumeKey, the helper starts afresh. The host keeps the pane mounted
//   while it is closed, or there is nothing to carry on with.

import { useEffect, useRef, useState } from 'react'
import useAgentSession, { type UseAgentSessionOptions } from './useAgentSession.js'
import { pageProfile } from './pageHelpers.js'
import type { PageToolCallInfo } from './types.js'
import { pageConfirmRule, runPageTool, toPageToolSpecs, UI_CALL, UI_SEARCH, type PageToolOutcome } from '../voice/uiBridge'
import type { VoiceUITool } from '../voice/types'

export const HELPER_CLOSED = 'The helper was closed.'
export const PERSON_DECLINED = 'The person declined.'
export const TURN_STOPPED = 'The turn was stopped.'

/** The control server refuses a longer page context (QueryRequestSchema, 8192); clip under it. */
export const MAX_PAGE_CONTEXT = 8000
const CLIPPED = '\n(clipped)'
export function clipPageContext(text: string): string {
  if (text.length <= MAX_PAGE_CONTEXT) return text
  return text.slice(0, MAX_PAGE_CONTEXT - CLIPPED.length) + CLIPPED
}
export const noSuchPageTool = (name: string) =>
  `There is no page tool called "${name}" on the page now. The person may have moved on, or the page has changed.`

export interface UsePageAgentOptions {
  /** A PAGE_HELPERS key, e.g. `tables`. */
  pageKey: string
  customer: string
  job: string
  /** The helper is open: opening starts afresh (D6; the chat is made at the first send) unless resumeKey says it is the same spot; closing answers held calls. */
  open: boolean
  /** Where the page is (the open table, the route). Reopened at the same key, the chat carries on. */
  resumeKey?: string
  /** What is on the page now, for the agent. Read at each send. */
  getPageContext: () => string
  /** The page's live tools (PageToolsProvider's livePageTools). Read at each send and each call. */
  getTools: () => VoiceUITool[]
  /** How the chat reaches the server: base URL, endpoints, auth, plugin event types. */
  sessionOptions?: UseAgentSessionOptions
  /** The profile the chat is made with. Absent: the page's own, `page-<pageKey>`. The Tables page names the Tables pane's below agent access (#1884). */
  profile?: string
}

/** A call waiting for the person's Confirm or Decline. */
export interface PendingPageConfirm {
  /** The id the result is posted under (the call's toolCallId). */
  callId: string
  name: string
  arguments: Record<string, unknown>
  /** The tool's own description, for the card. */
  description?: string
  /** What the call means in plain words ("Gender is Female"), from an ask tool's unconfirmed pass. */
  meaning?: string
}

const callKey = (c: PageToolCallInfo) => c.toolCallId || c.id

interface PendingSend {
  content: string
  model?: string
  attachmentIds?: string[]
  effort?: string
}

const findTool = (tools: VoiceUITool[], name: string) =>
  tools.find((t) => t.name === name && t.name !== UI_SEARCH && t.name !== UI_CALL && t.meta?.category !== 'bridge')

export default function usePageAgent(options: UsePageAgentOptions) {
  const raw = useAgentSession(options.sessionOptions)
  const rawRef = useRef(raw)
  rawRef.current = raw
  const optsRef = useRef(options)
  optsRef.current = options

  // toolCallIds this hook has dealt with (run, held, or seen at load).
  const handledRef = useRef(new Set<string>())
  // The session this hook created; only its calls run on the page.
  const ownSessionRef = useRef<string | null>(null)
  // The session id the call effect last saw; a change snapshots the calls as handled.
  const seenSessionRef = useRef<string | null | undefined>(undefined)
  // Calls the page holds (waiting for a click, or running), by callId.
  const heldRef = useRef(new Map<string, PageToolCallInfo>())
  const [pendingConfirms, setPendingConfirms] = useState<PendingPageConfirm[]>([])
  // Messages sent before this hook's chat exists, oldest first.
  const pendingSendsRef = useRef<PendingSend[]>([])
  // Counts chat creations: a creation another one (New chat) overtook is ignored.
  const createSeqRef = useRef(0)
  const creatingRef = useRef(false)
  // The resumeKey the helper was last closed at.
  const closedAtRef = useRef<string | null>(null)

  const syncWaiting = () => rawRef.current.setPageCallWaiting(heldRef.current.size > 0)

  const post = (callId: string, outcome: PageToolOutcome) => {
    // A 404 means the call is gone (timed out, or a new chat); nothing to do.
    rawRef.current.postPageToolResult(callId, outcome).catch((e) => {
      console.warn('[usePageAgent] page tool result not delivered:', callId, e)
    })
  }

  /** Stop holding a call. False when it was not held (already answered or released). */
  const release = (callId: string): boolean => {
    const had = heldRef.current.delete(callId)
    if (had) syncWaiting()
    return had
  }

  const releaseAll = (error: string) => {
    const ids = [...heldRef.current.keys()]
    heldRef.current.clear()
    setPendingConfirms([])
    for (const id of ids) post(id, { ok: false, error })
    if (ids.length > 0) rawRef.current.setPageCallWaiting(false)
  }

  const runHeld = async (callId: string, tool: VoiceUITool, args: Record<string, unknown>, confirmedByUser: boolean) => {
    const outcome = await runPageTool(tool, args, { confirmedByUser })
    // Released meanwhile (the helper closed, a new chat): already answered.
    if (release(callId)) post(callId, outcome)
  }

  // An ask tool unconfirmed only describes (set_quick_filter answers with
  // `meaning`); put that on the card. No meaning: the card keeps the arguments.
  const describeHeld = async (callId: string, tool: VoiceUITool, args: Record<string, unknown>) => {
    const outcome = await runPageTool(tool, args, { confirmedByUser: false })
    const r = outcome.ok ? outcome.result : undefined
    const meaning = r && typeof r === 'object' && typeof (r as { meaning?: unknown }).meaning === 'string' ? (r as { meaning: string }).meaning : ''
    if (!meaning) return
    setPendingConfirms((prev) => prev.map((p) => (p.callId === callId ? { ...p, meaning } : p)))
  }

  const handleCall = (call: PageToolCallInfo) => {
    const callId = callKey(call)
    const tool = findTool(optsRef.current.getTools(), call.name)
    if (!tool) {
      post(callId, { ok: false, error: noSuchPageTool(call.name) })
      return
    }
    heldRef.current.set(callId, call)
    syncWaiting()
    const rule = pageConfirmRule(tool)
    if (rule) {
      setPendingConfirms((prev) => [...prev, { callId, name: call.name, arguments: call.arguments, description: tool.description }])
      if (rule === 'ask') void describeHeld(callId, tool, call.arguments)
      return
    }
    void runHeld(callId, tool, call.arguments, false)
  }

  // Trigger: a new page tool call (or a different session). Runs each unseen
  // call once; never one that was there when the session was first seen.
  const sessionId = raw.session?.id ?? null
  useEffect(() => {
    const calls = rawRef.current.pageToolCalls
    const handled = handledRef.current
    if (sessionId !== seenSessionRef.current) {
      seenSessionRef.current = sessionId
      for (const c of calls) handled.add(callKey(c))
      return
    }
    for (const c of calls) {
      const key = callKey(c)
      if (handled.has(key)) continue
      handled.add(key)
      // Only a chat this helper started drives the page.
      if (!sessionId || sessionId !== ownSessionRef.current) continue
      handleCall(c)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw.pageToolCalls, sessionId])

  // Trigger: the chat's tool calls changing, and the turn ending. A held call
  // whose tool call is over (the control server answered it: a timeout, a
  // Stop), or whose turn has ended, is dropped without an answer.
  const isStreaming = raw.isStreaming
  const wasStreamingRef = useRef(isStreaming)
  useEffect(() => {
    const turnEnded = wasStreamingRef.current && !isStreaming
    wasStreamingRef.current = isStreaming
    if (heldRef.current.size === 0) return
    const over = new Set<string>()
    for (const m of rawRef.current.messages ?? []) {
      for (const tc of m.toolCalls ?? []) if (tc.status !== 'running' && heldRef.current.has(tc.id)) over.add(tc.id)
    }
    if (turnEnded) for (const id of heldRef.current.keys()) over.add(id)
    if (over.size === 0) return
    for (const id of over) heldRef.current.delete(id)
    setPendingConfirms((prev) => prev.filter((p) => !over.has(p.callId)))
    syncWaiting()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw.messages, isStreaming])

  // Send what waited for the chat, once this hook's chat is the session drawn.
  // useAgentSession's sendMessage reads the session from its own render, so it
  // is taken from the render that has the new chat (rawRef), never the one the
  // creation started in.
  const deliverPending = () => {
    const own = ownSessionRef.current
    const r = rawRef.current
    if (!own || r.session?.id !== own || pendingSendsRef.current.length === 0) return
    const sends = pendingSendsRef.current
    pendingSendsRef.current = []
    for (const p of sends) void r.sendMessage(p.content, p.model, p.attachmentIds, p.effort, pageSend)
  }

  const createOwn = async () => {
    const seq = ++createSeqRef.current
    creatingRef.current = true
    const { customer, job, pageKey, profile } = optsRef.current
    const id = await rawRef.current.createSession({ customer, job, profile: profile ?? pageProfile(pageKey) })
    if (seq !== createSeqRef.current) return
    creatingRef.current = false
    ownSessionRef.current = id
    // Not made: useAgentSession shows why; what waited is dropped.
    if (!id) pendingSendsRef.current = []
    deliverPending()
  }

  const startNew = async () => {
    releaseAll(HELPER_CLOSED)
    pendingSendsRef.current = []
    ownSessionRef.current = null
    await createOwn()
  }

  // Trigger: the helper opening (carry on at the same spot, else start afresh,
  // D6) and closing (answer what is held, drop what waited for a chat, note
  // where it closed).
  useEffect(() => {
    if (!options.open) return
    const key = optsRef.current.resumeKey
    const sameSpot = key !== undefined && key === closedAtRef.current
    if (ownSessionRef.current && !sameSpot) {
      ownSessionRef.current = null
      rawRef.current.clearSession()
    }
    return () => {
      releaseAll(HELPER_CLOSED)
      pendingSendsRef.current = []
      closedAtRef.current = optsRef.current.resumeKey ?? null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.open])

  // Trigger: the drawn session changing. The chat made by the first send is
  // drawn: send what waited for it.
  useEffect(() => {
    deliverPending()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  const confirm = (callId: string) => {
    const call = heldRef.current.get(callId)
    setPendingConfirms((prev) => prev.filter((p) => p.callId !== callId))
    if (!call) return
    const tool = findTool(optsRef.current.getTools(), call.name)
    if (!tool) {
      if (release(callId)) post(callId, { ok: false, error: noSuchPageTool(call.name) })
      return
    }
    void runHeld(callId, tool, call.arguments, true)
  }

  const decline = (callId: string) => {
    setPendingConfirms((prev) => prev.filter((p) => p.callId !== callId))
    if (release(callId)) post(callId, { ok: false, error: PERSON_DECLINED })
  }

  // Stop: answer what is held, then cancel the turn.
  const stop = async () => {
    releaseAll(TURN_STOPPED)
    await rawRef.current.cancelSession()
  }

  // Every message carries the page's whole tool list (the same every turn, D3)
  // and what is on the page now (D4), read when the turn is posted: a message
  // queued behind a running turn gets the page as it is when it goes.
  const pageSend = () => {
    const { getTools, getPageContext } = optsRef.current
    const pageContext = clipPageContext(getPageContext())
    return { pageTools: toPageToolSpecs(getTools()), ...(pageContext ? { pageContext } : {}) }
  }
  // No chat yet: the message waits while the first send makes one.
  const sendMessage: typeof raw.sendMessage = async (content, model, attachmentIds, effort) => {
    const own = ownSessionRef.current
    if (own && rawRef.current.session?.id === own) return rawRef.current.sendMessage(content, model, attachmentIds, effort, pageSend)
    pendingSendsRef.current.push({ content, model, attachmentIds, effort })
    if (own) return
    if (!creatingRef.current) await createOwn()
  }

  return {
    session: { ...raw, sendMessage },
    pendingConfirms,
    confirm,
    decline,
    startNew,
    stop,
  }
}
